import { useId, useMemo } from 'react'
import { toast } from 'sonner'
import Select from 'react-select'
import { REACT_SELECT_STYLES } from '@/components/common/reactSelectStyles'
import { isHexColor } from '../../../../../shared/pkg/prepacks/templateBuilder.js'

const COMBINING_ACCENTS_PATTERN = /[\u0300-\u036f]/g

// The app's react-select look (reactSelectStyles), sized and colored like the builder's own fields (FIELD, h-8).
// The menu is portaled to the body, outside .prepacks-workspace, so what it draws uses the --wf-* tokens the
// --prepacks-* ones are made of.
const TEAM_SELECT_STYLES = {
  ...REACT_SELECT_STYLES,
  control: (base, state) => ({
    ...REACT_SELECT_STYLES.control(base, state),
    transition: 'border-color 100ms, box-shadow 100ms',
    minHeight: 32,
    borderRadius: 8,
    borderColor: state.isFocused ? 'var(--wf-color-action)' : 'var(--prepacks-border)',
    backgroundColor: 'var(--prepacks-bg)',
    boxShadow: state.isFocused ? '0 0 0 3px color-mix(in srgb, var(--wf-color-action) 24%, transparent)' : 'none',
    // The size comes from the container's classes: 16px on a phone, where a smaller focused input makes iOS zoom in.
    fontSize: 'inherit',
    opacity: state.isDisabled ? 0.6 : 1,
    '&:hover': { borderColor: state.isFocused ? 'var(--wf-color-action)' : 'var(--prepacks-accent-text)' },
  }),
  valueContainer: (base) => ({ ...base, padding: '0 8px' }),
  clearIndicator: (base, state) => ({ ...REACT_SELECT_STYLES.clearIndicator(base, state), padding: 6 }),
  dropdownIndicator: (base, state) => ({ ...REACT_SELECT_STYLES.dropdownIndicator(base, state), padding: 6 }),
  // Above the builder's sticky header and preview (z-20, z-10).
  menuPortal: (base) => ({ ...base, zIndex: 30 }),
  groupHeading: (base) => ({ ...base, color: 'var(--wf-color-text-muted)', fontSize: 11, fontWeight: 600 }),
  option: (base, state) => ({ ...REACT_SELECT_STYLES.option(base, state), fontSize: 12, padding: '6px 10px' }),
}

// A league's heading where its catalog id is not what operators call it (#611).
const LEAGUE_LABELS = { EPL: 'Premier League', LALIGA: 'La Liga', RUGBY: 'Rugby' }
const leagueLabel = (league) => LEAGUE_LABELS[league] || league

/** Catalog teams as the picker groups them: one group per league, in the order the server sorts them (league, then name). */
function teamGroups(teams) {
  const groups = new Map()
  for (const team of teams) {
    if (typeof team?.id !== 'string' || typeof team.name !== 'string') continue
    const league = team.league || 'Other'
    if (!groups.has(league)) groups.set(league, [])
    groups.get(league).push({ value: team.id, label: team.name, team })
  }
  return [...groups].map(([league, options]) => ({ label: leagueLabel(league), options }))
}

/** A team in the picker: its two main colors, then its name. The swatches keep a fixed width (an empty slot for a missing color) so the names line up. */
function teamOptionLabel({ label, team }) {
  return (
    <span className="flex min-w-0 items-center gap-2">
      <span className="flex w-[26px] shrink-0 gap-0.5" aria-hidden="true">
        {[0, 1].map((index) => {
          const color = team?.colors?.[index]
          return <span key={index} className="h-3 w-3 rounded-sm border border-[var(--wf-color-border)]" style={color ? { background: color } : undefined} />
        })}
      </span>
      <span className="truncate">{label}</span>
    </span>
  )
}

/**
 * The palette a picked team gives: its first two catalog colors. The accent
 * stays as it is when the team has no second color, or only plain black or
 * white, which says nothing about the team.
 */
function teamPalette(team, palette) {
  const [main, accent] = (Array.isArray(team?.colors) ? team.colors : []).map((color) => String(color).toLowerCase())
  if (!isHexColor(main)) return palette
  return [main, isHexColor(accent) && accent !== '#000000' && accent !== '#ffffff' ? accent : palette[1]]
}

// The menu leaves the Colors panel, so it never stretches the panel's scroll area or opens under the sticky header.
const TEAM_MENU_TARGET = typeof document !== 'undefined' ? document.body : undefined
const noTeamMatches = ({ inputValue }) => (inputValue ? `No team matches “${inputValue}”` : 'No teams available')
const loadingTeams = () => 'Loading teams…'
// Lower case without accents, so "montreal" finds "Montréal".
const searchText = (value) => String(value || '').normalize('NFD').replace(COMBINING_ACCENTS_PATTERN, '').toLowerCase()

/**
 * Whether a team answers what was typed: its name, nickname or catalog id suffix ("dal") contains it, or its
 * league or the league's heading starts with it, so "nba" lists the NBA and not the WNBA, and "epl" and "premier"
 * both list the Premier League. Matching the whole id would let "nba" find "wnba:lv". A league typed whole lists
 * all its teams under their heading.
 */
function teamMatches({ data }, inputValue) {
  const typed = searchText(inputValue).trim()
  if (!typed) return true
  const team = data?.team
  const suffix = String(data?.value || '').split(':').at(-1)
  return [data?.label, team?.nickname, suffix].some((field) => searchText(field).includes(typed))
    || [team?.league, leagueLabel(team?.league)].some((league) => searchText(league).startsWith(typed))
}

/**
 * A catalog team (#602), grouped by league and searchable: the template's team in the Colors tab, the preview's in
 * Sample content. A set team shows even while the list loads or after it failed, so it never looks unset: by the
 * preview's answer for it when there is one, else by its id ("nfl:dal" -> "DAL"). A failed list says so under the
 * control, with a Retry, and the control stays usable. Only the instance with `announce` says it as an alert, so
 * one failure is not announced twice. `labelAction` sits at the end of the label's row.
 */
export function TeamSelect({ label, ariaLabel, name, value: teamId, teamsQuery, previewTeam, disabled, clearable = true, placeholder = 'No team', announce = false, labelAction = null, onChange }) {
  const inputId = useId()
  const teams = teamsQuery.data
  const groups = useMemo(() => teamGroups(teams || []), [teams])
  const value = useMemo(() => {
    if (!teamId) return null
    const listed = groups.flatMap((group) => group.options).find((option) => option.value === teamId)
    if (listed) return listed
    const dressed = previewTeam?.id === teamId ? previewTeam : undefined
    return { value: teamId, label: dressed?.name || teamId.split(':').at(-1).toUpperCase(), team: dressed }
  }, [groups, teamId, previewTeam])
  return (
    <>
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <label htmlFor={inputId} className="block text-[var(--prepacks-text)]">{label}</label>
        {labelAction}
      </div>
      {/* The control shows focus with its own halo; the theme's outline on the input inside it would double it. */}
      <Select inputId={inputId} aria-label={ariaLabel} name={name} classNamePrefix={`${name}-select`} className="text-base sm:text-xs [&_input]:!outline-none" options={groups} value={value} onChange={onChange}
        isClearable={clearable} isSearchable isDisabled={disabled} isLoading={teamsQuery.isFetching} placeholder={teamsQuery.isLoading ? 'Loading teams…' : placeholder}
        filterOption={teamMatches} noOptionsMessage={noTeamMatches} loadingMessage={loadingTeams} formatOptionLabel={teamOptionLabel}
        menuPlacement="auto" menuPosition="fixed" menuPortalTarget={TEAM_MENU_TARGET} styles={TEAM_SELECT_STYLES} />
      {teamsQuery.isError ? (
        <p role={announce ? 'alert' : 'status'} className="mt-1.5 text-[var(--wf-color-danger)]">
          Teams could not be loaded.{' '}
          <button type="button" disabled={teamsQuery.isFetching} onClick={() => void teamsQuery.refetch()} className="min-h-6 underline underline-offset-2 disabled:opacity-50">Retry</button>
        </p>
      ) : null}
    </>
  )
}

/**
 * The template's team (#602): picking one sets the two colors from its catalog colors and makes it the preview's
 * sample team. `onChange` is the choices' state setter: the colors' Undo applies to the choices as they are by then.
 */
export function TeamPicker({ choices, teamsQuery, previewTeam, disabled, onChange }) {
  const pick = (option) => {
    if (!option) {
      // Clearing the team leaves the colors it gave as they are.
      onChange({ ...choices, team: '' })
      return
    }
    const previous = choices.palette
    const palette = teamPalette(option.team, previous)
    onChange({ ...choices, team: option.value, palette })
    if (palette[0] === previous[0] && palette[1] === previous[1]) return
    // The colors it replaced may have been tuned by hand: say so, and offer them back unless they have changed since.
    toast(`Colors set from ${option.team?.name || option.label}`, {
      id: 'template-team-colors',
      action: {
        label: 'Undo',
        onClick: () => onChange((current) => (current.palette[0] === palette[0] && current.palette[1] === palette[1] ? { ...current, palette: previous } : current)),
      },
    })
  }
  return (
    <div className="text-xs">
      <TeamSelect label="Team" name="template-team" value={choices.team} teamsQuery={teamsQuery} previewTeam={previewTeam} disabled={disabled} announce onChange={pick} />
      <p className="mt-1.5 text-[var(--prepacks-muted)]">Picking a team sets the two colors below and shows it on the preview.</p>
    </div>
  )
}
